import { Injectable } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { guid } from '@datorama/akita';
import { AkitaNgFormsManager } from '@datorama/akita-ng-forms-manager';
import { ComponentFormController } from '@lycoris-nubila/ngx-lycoris';
import { TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest, from } from 'rxjs';
import {
	finalize,
	first,
	map,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs/operators';

import { MessageApplicationPort } from 'application/message/port/message-application.port';
import { SaveDocumentCmd } from 'document/command/save-document.cmd';
import { DocumentApplicationPort } from 'document/port/document-application.port';
import { CreateModuleCmd } from 'module/command/create-module.cmd';
import { ModuleDocumentOutput } from 'module/model/module-document.output';
import { ModuleApplicationPort } from 'module/port/module-application.port';
import { UserApplicationPort } from 'user/port/user-application.port';
import { IUser } from 'user/user';

import { ChatUploadState, ChatUploadStore } from './chat-upload.store';

@Injectable()
export class ChatUploadController extends ComponentFormController<
	ChatUploadState,
	any
> {
	public fileName$: Observable<string>;
	public isCreating$: Observable<boolean>;
	public isUploading$: Observable<boolean>;

	constructor(
		store: ChatUploadStore,
		private _fb: FormBuilder,
		formsManager: AkitaNgFormsManager,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort,
		private _moduleApplicationPort: ModuleApplicationPort,
		private _messageApplicationPort: MessageApplicationPort,
		private _documentApplicationPort: DocumentApplicationPort
	) {
		super(
			store,
			formsManager,
			`chat-upload-${guid()}`,
			_fb.group({
				title: ['', Validators.required],
				description: [''],
				document: [null, Validators.required],
				message: [''],
			})
		);

		this._initObservables();
	}

	public createDocument(targetUser: IUser): Promise<void> {
		this.store.update({ isCreating: true });

		return from(
			this._documentApplicationPort.saveDocument(this._buildSaveDocumentCommand())
		)
			.pipe(
				switchMap((document) =>
					this._messageApplicationPort.sendMessage({
						content: this.form.value.message,
						documentId: document.id,
						participantsId: [targetUser.id],
					})
				),
				tap((_) =>
					this.form.reset({
						title: '',
						description: '',
						document: null,
						message: '',
					})
				),
				finalize(() => this.store.update({ isCreating: false }))
			)
			.toPromise();
	}

	public createModule(targetUser: IUser): Promise<void> {
		this.store.update({ isCreating: true });

		return from(
			this._moduleApplicationPort.createModule(
				this._buildCreateModuleCommand(targetUser)
			)
		)
			.pipe(
				first(),
				switchMap((module) =>
					this._messageApplicationPort.sendMessage({
						content: this.form.value.message,
						moduleId: module.id,
						participantsId: [targetUser.id],
					})
				),
				tap((_) =>
					this.form.reset({
						title: '',
						description: '',
						document: null,
						message: '',
					})
				),
				finalize(() => this.store.update({ isCreating: false }))
			)
			.toPromise();
	}

	public generateDocumentUrl(file: File): void {
		this.store.update({ isUploading: true });

		from(this._moduleApplicationPort.generateModuleDocumentUrl(file))
			.pipe(
				tap((document) =>
					this.form.patchValue({
						document: {
							...document,
							mimeType: file.type,
						} as ModuleDocumentOutput,
						title: this.form.value.title ? this.form.value.title : document.fileName,
					})
				),
				finalize(() => this.store.update({ isUploading: false }))
			)
			.subscribe();
	}

	private _buildSaveDocumentCommand(id?: string): SaveDocumentCmd {
		return {
			type: this.form.value.document.mimeType.startsWith('image')
				? 'IMAGE'
				: 'DOCUMENT',
			localizations: [
				{
					locale: this._translateService.currentLang,
					name: this.form.value.document.fileName,
					url: this.form.value.document.url,
				},
			],
			id,
		};
	}

	private _buildCreateModuleCommand(targetUser: IUser): CreateModuleCmd {
		return {
			coacheeId: targetUser.isCoachee ? targetUser.id : null,
			moduleTitle: this.form.value.title,
			locale: this._translateService.currentLang,
			documentName: this.form.value.document.fileName,
			documentType: this.form.value.document.mimeType.startsWith('image')
				? 'IMAGE'
				: 'DOCUMENT',
			documentUrl: this.form.value.document.url,
			editable: true,
			moduleDescription: this.form.value.description,
		};
	}

	private _initObservables() {
		this.fileName$ = combineLatest([
			this._translateService.stream('presentation.app.chat.upload.file.select'),
			this.selectValue('document'),
		]).pipe(
			map(([selectLabel, document]: [string, ModuleDocumentOutput]) =>
				document ? document.fileName : selectLabel
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isUploading$ = this.select('isUploading');

		this.isCreating$ = this.select('isCreating');
	}
}
